import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContextMySQL';
import { authHeaders, API_URL } from '../api';

const NotificationContext = createContext();

// Connexion Socket.io pour notifications
const notificationSocket = io(API_URL, {
  autoConnect: false,
  reconnection: true,
  reconnectionDelay: 1000,
  reconnectionAttempts: 5,
});

export function NotificationProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);

  // Connexion Socket.io pour notifications
  useEffect(() => {
    if (isAuthenticated && user) {
      notificationSocket.auth = { token: user.token };
      notificationSocket.connect();

      notificationSocket.on('connect', () => {
        console.log('Connecté au serveur de notifications');
        setIsConnected(true);
      });

      notificationSocket.on('disconnect', () => {
        console.log('Déconnecté du serveur de notifications');
        setIsConnected(false);
      });

      notificationSocket.on('new_notification', (notification) => {
        setNotifications(prev => [notification, ...prev]);
        setUnreadCount(prev => prev + 1);
        
        // Afficher une notification toast dans le navigateur
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('Nouveau message', {
            body: notification.message?.content || 'Vous avez un nouveau message',
            icon: '/favicon.ico'
          });
        }
      });

      notificationSocket.on('unread_count', ({ count }) => {
        setUnreadCount(count);
      });

      // Charger les notifications existantes
      loadNotifications();
    }

    return () => {
      notificationSocket.disconnect();
    };
  }, [isAuthenticated, user]);

  const loadNotifications = useCallback(async () => {
    if (!user) return;
    try {
      // Cette API sera ajoutée au serveur Socket.io
      const response = await fetch(`${API_URL}/api/notifications/${user.id}`, { headers: authHeaders() });
      const data = await response.json();
      if (data.success) {
        setNotifications(data.notifications);
        const unread = data.notifications.filter(n => !n.is_read).length;
        setUnreadCount(unread);
      }
    } catch (error) {
      console.error('Erreur chargement notifications:', error);
    }
  }, [user]);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      await fetch(`${API_URL}/api/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: authHeaders()
      });
      
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, is_read: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Erreur marquer notification comme lue:', error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!user) return;
    try {
      await fetch(`${API_URL}/api/notifications/${user.id}/read-all`, {
        method: 'POST',
        headers: authHeaders()
      });
      
      setNotifications(prev => 
        prev.map(n => ({ ...n, is_read: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Erreur marquer toutes notifications comme lues:', error);
    }
  }, [user]);

  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return false;
  }, []);

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      isConnected,
      loadNotifications,
      markAsRead,
      markAllAsRead,
      requestNotificationPermission,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
}
